import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Communication, CommunicationFilters } from '@/types/communications';
import { startOfDayInTz, endOfDayInTz, subDays } from '@/utils/timezone';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface UseCommunicationsOptions {
  filters: CommunicationFilters;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}

interface CustomerCommunicationRow {
  id: string;
  customer_id: string;
  lead_id: string | null;
  job_id: string | null;
  communication_type: string;
  direction: string | null;
  subject: string | null;
  content: string | null;
  outcome: string | null;
  created_at: string | null;
  created_by: string | null;
  customers: { customer_name: string; primary_phone: string } | null;
  leads: { lead_number: string } | null;
}

interface CallLogRow {
  id: string;
  customer_id: string | null;
  lead_id: string | null;
  direction: string | null;
  started_at: string | null;
  created_at: string | null;
  talk_time_seconds: number | null;
  recording_available: boolean | null;
  external_call_id: string | null;
  is_new_caller: boolean | null;
  call_score: number | null;
  campaign_id: string | null;
  agent_id: string | null;
  tracking_label: string | null;
  customers: { customer_name: string; primary_phone: string | null } | null;
  leads: { customer_name: string | null; customer_phone: string | null; lead_number: string | null } | null;
  campaigns: { name: string } | null;
  agent: { full_name: string | null } | null;
}

interface SmsRow {
  id: string;
  customer_id: string | null;
  lead_id: string | null;
  direction: string | null;
  content: string | null;
  sent_at: string | null;
  created_at: string | null;
  from_number: string | null;
  to_number: string | null;
  sender_id: string | null;
  campaign_id: string | null;
  customers: { customer_name: string; primary_phone: string | null } | null;
  leads: { customer_name: string | null; customer_phone: string | null; lead_number: string | null } | null;
  campaigns: { name: string } | null;
  sender: { full_name: string | null } | null;
}

interface EmailRow {
  id: string;
  customer_id: string | null;
  lead_id: string | null;
  direction: string | null;
  subject: string | null;
  content: string | null;
  sent_at: string | null;
  created_at: string | null;
  sender_id: string | null;
  customers: { customer_name: string; primary_phone: string | null } | null;
  leads: { customer_name: string | null; customer_phone: string | null; lead_number: string | null } | null;
  sender: { full_name: string | null } | null;
}

// "Today" / "yesterday" are the company's calendar days, not the viewer's browser.
function getDateRange(filters: CommunicationFilters, timezone: string): { start: Date; end: Date } {
  const now = new Date();
  const startOfDay = (date: Date) => startOfDayInTz(date, timezone);

  switch (filters.dateRange) {
    case 'today':
      return { start: startOfDay(now), end: now };
    case 'yesterday':
      return { start: startOfDay(subDays(now, 1)), end: endOfDayInTz(subDays(now, 1), timezone) };
    case 'last7days':
      return { start: startOfDay(subDays(now, 7)), end: now };
    case 'last30days':
      return { start: startOfDay(subDays(now, 30)), end: now };
    case 'custom':
      return {
        start: filters.startDate || startOfDay(subDays(now, 7)),
        end: filters.endDate || now,
      };
    default:
      return { start: startOfDay(subDays(now, 7)), end: now };
  }
}

async function fetchCustomerCommunications(
  dateRange: { start: Date; end: Date },
  filters: CommunicationFilters,
  windowSize: number
): Promise<Communication[]> {
  // Removed profiles:created_by join - FK doesn't exist, causes 400 error
  let query = supabase
    .from('customer_communications')
    .select(`
      id,
      customer_id,
      lead_id,
      job_id,
      communication_type,
      direction,
      subject,
      content,
      outcome,
      created_at,
      created_by,
      customers:customer_id(customer_name, primary_phone),
      leads:lead_id(lead_number)
    `)
    .gte('created_at', dateRange.start.toISOString())
    .lte('created_at', dateRange.end.toISOString())
    .order('created_at', { ascending: false })
    .range(0, windowSize - 1);

  // Filter by type if specified
  if (filters.type !== 'all') {
    query = query.eq('communication_type', filters.type);
  }

  if (filters.direction !== 'all') {
    query = query.eq('direction', filters.direction);
  }

  if (filters.search) {
    query = query.or(`content.ilike.%${filters.search}%,subject.ilike.%${filters.search}%`);
  }

  if (filters.csrId) {
    query = query.eq('created_by', filters.csrId);
  }

  const { data, error } = await query;
  if (error) throw error;

  const rows = data as unknown as CustomerCommunicationRow[] || [];

  // Fetch profile names separately (no FK relationship available)
  const createdByIds = [...new Set(rows.map(r => r.created_by).filter(Boolean))] as string[];
  let profileMap: Record<string, string> = {};

  if (createdByIds.length > 0) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', createdByIds);

    if (profiles) {
      profileMap = Object.fromEntries(profiles.map(p => [p.id, p.full_name || '']));
    }
  }

  return rows.map((comm): Communication => ({
    id: comm.id,
    source: 'customer_communication',
    type: comm.communication_type as Communication['type'],
    direction: comm.direction as 'inbound' | 'outbound' | null,
    timestamp: comm.created_at || new Date().toISOString(),
    customer_name: comm.customers?.customer_name || 'Unknown',
    customer_phone: comm.customers?.primary_phone || '',
    customer_id: comm.customer_id,
    lead_id: comm.lead_id || undefined,
    job_id: comm.job_id || undefined,
    lead_number: comm.leads?.lead_number,
    content: comm.content || undefined,
    subject: comm.subject || undefined,
    outcome: comm.outcome || undefined,
    agent_name: comm.created_by ? profileMap[comm.created_by] : undefined,
  }));
}

async function fetchCallLogs(
  dateRange: { start: Date; end: Date },
  filters: CommunicationFilters,
  windowSize: number
): Promise<Communication[]> {
  if (filters.type !== 'all' && filters.type !== 'call') return [];

  let query = supabase
    .from('call_logs')
    .select(`
      id,
      customer_id,
      lead_id,
      direction,
      started_at,
      created_at,
      talk_time_seconds,
      recording_available,
      recording_url,
      external_call_id,
      is_new_caller,
      call_score,
      campaign_id,
      agent_id,
      tracking_label,
      customers:customer_id(customer_name, primary_phone),
      leads:lead_id(customer_name, customer_phone, lead_number),
      campaigns:campaign_id(name),
      agent:profiles!call_logs_agent_id_fkey(full_name)
    `)
    // Filter on call time (started_at), not ingest time (created_at) — the CTM
    // poller can ingest older calls whose created_at would be "now".
    .gte('started_at', dateRange.start.toISOString())
    .lte('started_at', dateRange.end.toISOString())
    .order('started_at', { ascending: false })
    .range(0, windowSize - 1);

  if (filters.direction !== 'all') {
    query = query.eq('direction', filters.direction);
  }
  if (filters.csrId) {
    query = query.eq('agent_id', filters.csrId);
  }

  const { data, error } = await query;
  if (error) throw error;
  const rows = (data as unknown as CallLogRow[]) || [];

  const search = filters.search.trim().toLowerCase();
  return rows
    .map((row): Communication => ({
      id: row.id,
      source: 'call_log',
      type: 'call',
      direction: (row.direction as 'inbound' | 'outbound' | null) ?? null,
      timestamp: row.started_at || row.created_at || new Date().toISOString(),
      customer_name: row.customers?.customer_name || row.leads?.customer_name || 'Unknown',
      customer_phone: row.customers?.primary_phone || row.leads?.customer_phone || '',
      customer_id: row.customer_id || undefined,
      lead_id: row.lead_id || undefined,
      lead_number: row.leads?.lead_number || undefined,
      campaign_name: row.campaigns?.name || undefined,
      tracking_label: row.tracking_label || undefined,
      call_id: row.external_call_id || undefined,
      talk_time_seconds: row.talk_time_seconds || undefined,
      recording_available: row.recording_available || undefined,
      recording_url: row.recording_url || undefined,
      is_new_caller: row.is_new_caller ?? undefined,
      call_score: row.call_score ?? undefined,
      agent_name: row.agent?.full_name || undefined,
      agent_id: row.agent_id || undefined,
    }))
    .filter((row) => {
      if (!search) return true;
      return [
        row.customer_name,
        row.customer_phone,
        row.call_id,
        row.campaign_name,
        row.lead_number,
        row.tracking_label,
      ].some((v) => (v || '').toLowerCase().includes(search));
    });
}

async function fetchSmsCommunications(
  dateRange: { start: Date; end: Date },
  filters: CommunicationFilters,
  windowSize: number
): Promise<Communication[]> {
  if (filters.type !== 'all' && filters.type !== 'sms') return [];

  let query = supabase
    .from('sms_messages')
    .select(`
      id,
      customer_id,
      lead_id,
      direction,
      content,
      sent_at,
      created_at,
      from_number,
      to_number,
      sender_id,
      campaign_id,
      customers:customer_id(customer_name, primary_phone),
      leads:lead_id(customer_name, customer_phone, lead_number),
      campaigns:campaign_id(name),
      sender:profiles!sms_messages_sender_id_fkey(full_name)
    `)
    // Filter on message time (sent_at), not ingest time (created_at)
    .gte('sent_at', dateRange.start.toISOString())
    .lte('sent_at', dateRange.end.toISOString())
    .order('sent_at', { ascending: false })
    .range(0, windowSize - 1);

  if (filters.direction !== 'all') {
    query = query.eq('direction', filters.direction);
  }
  if (filters.csrId) {
    query = query.eq('sender_id', filters.csrId);
  }

  const { data, error } = await query;
  if (error) throw error;
  const rows = (data as unknown as SmsRow[]) || [];
  const search = filters.search.trim().toLowerCase();

  return rows
    .map((row): Communication => ({
      id: row.id,
      source: 'sms_message',
      type: 'sms',
      direction: (row.direction as 'inbound' | 'outbound' | null) ?? null,
      timestamp: row.sent_at || row.created_at || new Date().toISOString(),
      customer_name: row.customers?.customer_name || row.leads?.customer_name || 'Unknown',
      customer_phone:
        row.customers?.primary_phone || row.leads?.customer_phone || row.from_number || row.to_number || '',
      customer_id: row.customer_id || undefined,
      lead_id: row.lead_id || undefined,
      lead_number: row.leads?.lead_number || undefined,
      content: row.content || undefined,
      campaign_name: row.campaigns?.name || undefined,
      agent_name: row.sender?.full_name || undefined,
      agent_id: row.sender_id || undefined,
    }))
    .filter((row) => {
      if (!search) return true;
      return [
        row.customer_name,
        row.customer_phone,
        row.content,
        row.campaign_name,
        row.lead_number,
      ].some((v) => (v || '').toLowerCase().includes(search));
    });
}

async function fetchEmailCommunications(
  dateRange: { start: Date; end: Date },
  filters: CommunicationFilters,
  windowSize: number
): Promise<Communication[]> {
  if (filters.type !== 'all' && filters.type !== 'email') return [];

  let query = supabase
    .from('email_messages')
    .select(`
      id,
      customer_id,
      lead_id,
      direction,
      subject,
      content,
      sent_at,
      created_at,
      sender_id,
      customers:customer_id(customer_name, primary_phone),
      leads:lead_id(customer_name, customer_phone, lead_number),
      sender:profiles!email_messages_sender_id_fkey(full_name)
    `)
    .gte('created_at', dateRange.start.toISOString())
    .lte('created_at', dateRange.end.toISOString())
    .order('created_at', { ascending: false })
    .range(0, windowSize - 1);

  if (filters.direction !== 'all') {
    query = query.eq('direction', filters.direction);
  }
  if (filters.csrId) {
    query = query.eq('sender_id', filters.csrId);
  }
  if (filters.search) {
    query = query.or(`content.ilike.%${filters.search}%,subject.ilike.%${filters.search}%`);
  }

  const { data, error } = await query;
  if (error) throw error;
  const rows = (data as unknown as EmailRow[]) || [];

  return rows.map((row): Communication => ({
    id: row.id,
    source: 'customer_communication',
    type: 'email',
    direction: (row.direction as 'inbound' | 'outbound' | null) ?? null,
    timestamp: row.sent_at || row.created_at || new Date().toISOString(),
    customer_name: row.customers?.customer_name || row.leads?.customer_name || 'Unknown',
    customer_phone: row.customers?.primary_phone || row.leads?.customer_phone || '',
    customer_id: row.customer_id || undefined,
    lead_id: row.lead_id || undefined,
    lead_number: row.leads?.lead_number || undefined,
    subject: row.subject || undefined,
    content: row.content || undefined,
    agent_name: row.sender?.full_name || undefined,
    agent_id: row.sender_id || undefined,
  }));
}

export function useCommunications(options: UseCommunicationsOptions) {
  const { filters, limit = 50, offset = 0, enabled = true } = options;
  const { timezone } = useCompanyLocale();

  return useQuery({
    queryKey: ['communications', filters, limit, offset, timezone],
    enabled,
    queryFn: async (): Promise<{ communications: Communication[]; hasMore: boolean }> => {
      const dateRange = getDateRange(filters, timezone);
      const windowSize = Math.min(Math.max(offset + limit + 200, 300), 5000);

      // Fetch all comm channels and normalize into a single list for Logs/Timeline
      const [customerComms, callComms, smsComms, emailComms] = await Promise.all([
        fetchCustomerCommunications(dateRange, filters, windowSize),
        fetchCallLogs(dateRange, filters, windowSize),
        fetchSmsCommunications(dateRange, filters, windowSize),
        fetchEmailCommunications(dateRange, filters, windowSize),
      ]);

      const merged = [...customerComms, ...callComms, ...smsComms, ...emailComms].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      const paged = merged.slice(offset, offset + limit);
      const hasMore = merged.length > offset + limit;

      return { communications: paged, hasMore };
    },
    staleTime: 30000, // 30 seconds
  });
}

async function countCustomerCommunications(dateRange: { start: Date; end: Date }, filters: CommunicationFilters) {
  if (filters.type !== 'all' && ['call', 'sms'].includes(filters.type)) {
    return 0;
  }

  let query = supabase
    .from('customer_communications')
    .select('id', { count: 'exact', head: true })
    .gte('created_at', dateRange.start.toISOString())
    .lte('created_at', dateRange.end.toISOString());

  if (filters.type !== 'all' && !['call', 'sms'].includes(filters.type)) {
    query = query.eq('communication_type', filters.type);
  }
  if (filters.direction !== 'all') {
    query = query.eq('direction', filters.direction);
  }
  if (filters.search) {
    query = query.or(`content.ilike.%${filters.search}%,subject.ilike.%${filters.search}%`);
  }
  if (filters.csrId) {
    query = query.eq('created_by', filters.csrId);
  }

  const { count } = await query;
  return count || 0;
}

async function countCallLogs(dateRange: { start: Date; end: Date }, filters: CommunicationFilters) {
  if (filters.type !== 'all' && filters.type !== 'call') return 0;

  let query = supabase
    .from('call_logs')
    .select('id', { count: 'exact', head: true })
    .gte('started_at', dateRange.start.toISOString())
    .lte('started_at', dateRange.end.toISOString());

  if (filters.direction !== 'all') {
    query = query.eq('direction', filters.direction);
  }
  if (filters.csrId) {
    query = query.eq('agent_id', filters.csrId);
  }

  const { count } = await query;
  return count || 0;
}

async function countSms(dateRange: { start: Date; end: Date }, filters: CommunicationFilters) {
  if (filters.type !== 'all' && filters.type !== 'sms') return 0;

  let query = supabase
    .from('sms_messages')
    .select('id', { count: 'exact', head: true })
    .gte('sent_at', dateRange.start.toISOString())
    .lte('sent_at', dateRange.end.toISOString());

  if (filters.direction !== 'all') {
    query = query.eq('direction', filters.direction);
  }
  if (filters.csrId) {
    query = query.eq('sender_id', filters.csrId);
  }
  if (filters.search) {
    query = query.ilike('content', `%${filters.search}%`);
  }

  const { count } = await query;
  return count || 0;
}

async function countEmails(dateRange: { start: Date; end: Date }, filters: CommunicationFilters) {
  if (filters.type !== 'all' && filters.type !== 'email') return 0;

  let query = supabase
    .from('email_messages')
    .select('id', { count: 'exact', head: true })
    .gte('created_at', dateRange.start.toISOString())
    .lte('created_at', dateRange.end.toISOString());

  if (filters.direction !== 'all') {
    query = query.eq('direction', filters.direction);
  }
  if (filters.csrId) {
    query = query.eq('sender_id', filters.csrId);
  }
  if (filters.search) {
    query = query.or(`content.ilike.%${filters.search}%,subject.ilike.%${filters.search}%`);
  }

  const { count } = await query;
  return count || 0;
}

export function useCommunicationsCount(filters: CommunicationFilters) {
  const { timezone } = useCompanyLocale();
  return useQuery({
    queryKey: ['communications-count', filters, timezone],
    queryFn: async (): Promise<number> => {
      const dateRange = getDateRange(filters, timezone);
      const [manualCount, callCount, smsCount, emailCount] = await Promise.all([
        countCustomerCommunications(dateRange, filters),
        countCallLogs(dateRange, filters),
        countSms(dateRange, filters),
        countEmails(dateRange, filters),
      ]);
      return manualCount + callCount + smsCount + emailCount;
    },
    staleTime: 60000, // 1 minute
  });
}

